import { CheckCircleFilled, CheckCircleOutlined, DownloadOutlined, EyeOutlined, PrinterOutlined, SearchOutlined } from "@ant-design/icons";
import { Input, Select, Table, Tag, Tooltip, message } from "antd";
import axios from "axios";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectUser } from "../../../../../redux/slices/auth.slice";

const { Option } = Select;

const FORM_TYPE_LABEL = {
  transfer_certificate: "Transfer Certificate",
  study_certificate: "Study Certificate",
  conduct_certificate: "Conduct Certificate",
};

const FORM_TYPE_COLOR = {
  transfer_certificate: "blue",
  study_certificate: "green",
  conduct_certificate: "purple",
};

const STATUS_COLOR = {
  draft: "gold",
  finalized: "blue",
  delivered: "green",
};

const STATUS_LABEL = {
  draft: "Generated",
  finalized: "Validated",
  delivered: "Issued",
};


const CertBuilderList = () => {
  const user = useSelector(selectUser);
  const queryClient = useQueryClient();

  const [filterType, setFilterType] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  // ─── Query ────────────────────────────────────────────────────────────────────

  const docsQuery = useQuery(
    ["cb-documents", user?.school?._id, filterType, filterStatus],
    () => {
      const params = new URLSearchParams({ school: user?.school?._id });
      if (filterType) params.set("form_type", filterType);
      if (filterStatus) params.set("status", filterStatus);
      return axios
        .get(`${serverUrl}/cert-documents?${params}`, {
          headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
        })
        .then((r) => r.data);
    },
    { enabled: !!user?.school?._id }
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  function viewCert(id) {
    axios({
      url: `${serverUrl}/cert-documents/${id}/download`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
    })
      .then((res) => {
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        window.open(blobUrl, "_blank");
      })
      .catch(() => message.error("Failed to load PDF"));
  }

  function printCert(id) {
    axios({
      url: `${serverUrl}/cert-documents/${id}/download`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
    })
      .then((res) => {
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const iframe = document.createElement("iframe");
        iframe.style.cssText = "position:fixed;left:-9999px;top:0;width:800px;height:600px;border:none;";
        iframe.src = blobUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch {
            window.open(blobUrl, "_blank");
          }
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(blobUrl);
          }, 60000);
        };
      })
      .catch(() => message.error("Failed to load PDF for printing"));
  }

  async function updateStatus(id, newStatus) {
    setUpdatingId(id);
    try {
      await axios.patch(
        `${serverUrl}/cert-documents/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }
      );
      queryClient.invalidateQueries("cb-documents");
    } catch {
      message.error("Failed to update certificate status");
    } finally {
      setUpdatingId(null);
    }
  }

  // ─── Columns ──────────────────────────────────────────────────────────────────

  const issuedColumns = [
    {
      title: "File No",
      dataIndex: "file_number",
      key: "file_number",
      align: "center",
      render: (v) => (
        <div className="text-base text-center font-semibold" style={{ color: "#111111" }}>
          {v || "—"}
        </div>
      ),
    },
    {
      title: "Student",
      key: "student",
      align: "center",
      render: (_, r) => (
        <div className="text-base font-semibold text-center capitalize" style={{ color: "#111111" }}>
          {r.student ? `${r.student.first_name} ${r.student.last_name}` : "—"}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "form_type",
      key: "form_type",
      align: "center",
      render: (v) => (
        <Tag color={FORM_TYPE_COLOR[v] || "default"} style={{ textTransform: "capitalize" }}>
          {FORM_TYPE_LABEL[v] || (v || "").replace(/_/g, " ")}
        </Tag>
      ),
    },
    {
      title: "Template",
      key: "template",
      align: "center",
      render: (_, r) => (
        <div className="text-base text-center" style={{ color: "#616161" }}>
          {r.template?.name || "—"}
        </div>
      ),
    },
    {
      title: "Issued",
      dataIndex: "issued_at",
      key: "issued_at",
      align: "center",
      render: (v) => (
        <div className="text-base text-center" style={{ color: "#111111" }}>
          {v ? new Date(v).toLocaleDateString("en-IN") : "—"}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (v) => (
        <Tag color={STATUS_COLOR[v] || "default"}>{STATUS_LABEL[v] || v}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, r) => {
        const isValidated = r.status === "finalized" || r.status === "delivered";
        const iconStyle = { fontSize: 18, cursor: "pointer" };
        return (
          <div style={{ display: "flex", gap: 16, justifyContent: "center", alignItems: "center" }}>
            {/* View */}
            <Tooltip title="View">
              <EyeOutlined style={{ ...iconStyle, color: "#6b7280" }} onClick={() => viewCert(r._id)} />
            </Tooltip>

            {/* Validate */}
            <Tooltip title={isValidated ? "Validated" : "Mark as Validated"}>
              {isValidated ? (
                <CheckCircleFilled style={{ ...iconStyle, color: "#16a34a" }} />
              ) : (
                updatingId === r._id ? (
                  <CheckCircleOutlined style={{ ...iconStyle, color: "#d1d5db" }} />
                ) : (
                  <CheckCircleOutlined
                    style={{ ...iconStyle, color: "#f97316" }}
                    onClick={() => updateStatus(r._id, "finalized")}
                  />
                )
              )}
            </Tooltip>

            {/* Print */}
            <Tooltip title="Print">
              <PrinterOutlined
                style={{ ...iconStyle, color: "#6b7280" }}
                onClick={() => printCert(r._id)}
              />
            </Tooltip>

            {/* Download */}
            <Tooltip title="Download">
              <a href={`${serverUrl}/cert-documents/${r._id}/download`} target="_blank" rel="noreferrer" download>
                <DownloadOutlined style={{ ...iconStyle, color: "#f97316" }} />
              </a>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const rawDocs = docsQuery.data?.result?.data || [];
  const issuedDocs = rawDocs.filter((r) => {
    const q = searchText.toLowerCase();
    const matchSearch = !q ||
      `${r.student?.first_name} ${r.student?.last_name}`.toLowerCase().includes(q) ||
      (r.file_number || "").toLowerCase().includes(q);
    return matchSearch;
  });

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="mt-8">
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        <div className="text-2xl text-orange-500 font-semibold pb-6">
          Certificate Registry
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", marginBottom: 24 }}>
          <Input
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            placeholder="Search by student name or file no."
            allowClear
            style={{ width: 260 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Select
            allowClear
            showArrow
            placeholder="All Types"
            style={{ width: 200 }}
            onChange={setFilterType}
            value={filterType}
            getPopupContainer={(t) => t.parentNode}
          >
            <Option value="transfer_certificate">Transfer Certificate</Option>
            <Option value="study_certificate">Study Certificate</Option>
            <Option value="conduct_certificate">Conduct Certificate</Option>
          </Select>
          <Select
            allowClear
            showArrow
            placeholder="All Statuses"
            style={{ width: 160 }}
            onChange={setFilterStatus}
            value={filterStatus}
            getPopupContainer={(t) => t.parentNode}
          >
            <Option value="draft">Generated</Option>
            <Option value="finalized">Validated</Option>
            <Option value="delivered">Issued</Option>
          </Select>
        </div>

        <Table
          columns={issuedColumns}
          dataSource={issuedDocs}
          rowKey="_id"
          loading={docsQuery.isLoading}
          pagination={{ pageSize: 10 }}
        />
      </div>
    </div>
  );
};

export default CertBuilderList;
